import type {ReactNode} from 'react';
import {scoreCommandSearchFields} from '../match-query';
import {isNavigationItemVisible, resolveNavigationItemTo} from './resolve-navigation-item';
import type {CommandSearchContext, CommandSearchProvider, CommandSearchResult} from '../types';

export interface NavigationSearchItem {
    id: string;
    title: string;
    subtitle?: string;
    keywords?: string[];
    badge?: string;
    badgeColor?: CommandSearchResult['badgeColor'];
    icon?: ReactNode;
    /** Static route for global (unscoped) entries. */
    to?: string;
    /** Scoped route template — e.g. `/:customerId/invoices/list`. */
    routeTemplate?: string;
    /** Scope frame type that supplies route params (`customer`, `vendor`, ...). */
    scopeType?: string;
    /** Fully custom route resolver (overrides `to` / `routeTemplate`). */
    resolveTo?: (ctx: CommandSearchContext) => string | undefined;
    /** Access key checked via `ctx.services.canAccess` when the app injects it. */
    accessKey?: string;
    /** Per-item runtime access gate (runs after `accessKey` check). */
    canAccess?: (ctx: CommandSearchContext) => boolean;
    hide?: boolean | ((ctx: CommandSearchContext) => boolean);
    children?: NavigationSearchItem[];
}

export interface CreateNavigationProviderConfig {
    id: string;
    category: string;
    items: NavigationSearchItem[] | ((ctx: CommandSearchContext) => NavigationSearchItem[]);
    enabled?: CommandSearchProvider['enabled'];
    supersedes?: string[];
    priority?: number;
}

const navigateMeta = (to: string) => ({
    recentMeta: {
        actionKey: 'navigate',
        actionPayload: {to},
    },
});

/** Flattens nested navigation config into searchable leaf entries. */
export const flattenNavigationItems = (
    items: NavigationSearchItem[],
    ctx?: CommandSearchContext,
): NavigationSearchItem[] => {
    const flat: NavigationSearchItem[] = [];

    const walk = (entries: NavigationSearchItem[]) => {
        entries.forEach((entry) => {
            if (ctx && !isNavigationItemVisible(entry, ctx)) {
                return;
            }
            if (entry.children?.length) {
                walk(entry.children);
                return;
            }
            flat.push(entry);
        });
    };

    walk(items);
    return flat;
};

const resolveItems = (
    items: CreateNavigationProviderConfig['items'],
    ctx: CommandSearchContext,
): NavigationSearchItem[] => typeof items === 'function' ? items(ctx) : items;

/**
 * Creates a config-driven navigation provider for command search.
 * Items support scoped route templates, per-item access checks, and runtime visibility.
 */
export const createNavigationProvider = (config: CreateNavigationProviderConfig): CommandSearchProvider => ({
    id: config.id,
    category: config.category,
    priority: config.priority,
    supersedes: config.supersedes,
    enabled: config.enabled,
    search: (ctx: CommandSearchContext): CommandSearchResult[] => {
        const results: CommandSearchResult[] = [];
        const flatItems = flattenNavigationItems(resolveItems(config.items, ctx), ctx);

        flatItems.forEach((item) => {
            const to = resolveNavigationItemTo(item, ctx);
            if (!to) {
                return;
            }

            const score = scoreCommandSearchFields(ctx.query, {
                title: item.title,
                subtitle: item.subtitle,
                keywords: item.keywords,
                category: config.category,
            });
            if (score <= 0) {
                return;
            }

            results.push({
                id: item.id,
                category: config.category,
                title: item.title,
                subtitle: item.subtitle,
                keywords: item.keywords,
                badge: item.badge ?? 'Navigate',
                badgeColor: item.badgeColor ?? 'primary',
                icon: item.icon,
                score,
                providerId: config.id,
                to,
                ...navigateMeta(to),
                onSelect: () => ctx.navigate(to),
            });
        });

        return results;
    },
});
